import { Clock } from '../../ui/icons'
import { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import { CLOUD_API } from '@core/pages/admin/shared';

/**
 * Публичный адрес PWA для QR. В Android WebView origin — `https://localhost`,
 * поэтому для production он не годится: телефон откроет localhost у себя.
 */
function resolvePairingBaseUrl(): string {
  const configured = import.meta.env.VITE_PAIRING_APP_URL?.trim()
  if (configured) return configured.replace(/\/+$/, '')

  const { protocol, hostname, origin } = window.location
  const isLoopback = hostname === 'localhost' || hostname === '127.0.0.1'
  // Обычный браузер на публичном HTTPS-домене может использовать свой origin.
  if (protocol === 'https:' && !isLoopback) return origin
  // Dev-сервер на MacBook/ноутбуке — только для локальной проверки.
  if (import.meta.env.DEV) return origin

  throw new Error(
    'Не задан VITE_PAIRING_APP_URL. Укажите публичный HTTPS-адрес PWA для QR.'
  )
}

interface PairingModalProps {
  isOpen: boolean;
  onClose: () => void;
  token: string;
  /**
   * Зона (рабочее место/отдел) терминала — её получит сопрягаемое устройство,
   * чтобы делиться общим пулом вскрытой тары.
   */
  zoneId: string;
  /** Название организации для текста модалки. */
  companyName?: string;
}

/**
 * Модалка сопряжения кассового терминала через одноразовый QR-код.
 *
 * Привязывает устройство к организации (tenant), не к кассиру.
 * Показывает SVG QR со ссылкой на публичный HTTPS-адрес PWA и отсчёт TTL
 * (5 мин). `window.location.origin` нельзя использовать на Android WebView:
 * там origin равен `https://localhost`, и телефон откроет свой localhost.
 */
export const PairingModal = ({
  isOpen,
  onClose,
  token,
  zoneId,
  companyName,
}: PairingModalProps) => {
  const [qrSvg, setQrSvg] = useState<string>('');
  const [timeLeft, setTimeLeft] = useState<number>(300); // 5 минут = 300 сек
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setTimeLeft(300);
      setError(null);
      return;
    }
    if (!token) {
      setError('Касса не авторизована в облаке');
      return;
    }

    let cancelled = false;
    setQrSvg('');
    void (async () => {
      try {
        // Одноразовый код создаёт backend; device-token в QR не попадает.
        const response = await fetch(`${CLOUD_API}/auth/pairing-codes`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ zone_id: zoneId })
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = (await response.json()) as { code: string };
        const pairingBase = resolvePairingBaseUrl();
        const pairingUrl = `${pairingBase}/?pair_code=${encodeURIComponent(data.code)}`;
        const svg = await QRCode.toString(pairingUrl, {
          type: 'svg',
          margin: 1,
          color: {
            dark: '#0f172a',
            light: '#ffffff'
          }
        });
        if (!cancelled) setQrSvg(svg);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Не удалось создать код сопряжения');
        }
      }
    })();

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [isOpen, token, zoneId]);

  if (!isOpen) return null;

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  const timeFormatted = `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;

  return (
    <div className="fixed inset-0 z-camera flex items-center justify-center bg-ink/60 p-4 backdrop-blur-sm">
      <div className="w-full max-w-[400px] rounded-card bg-white p-7 text-center shadow-modal">
        <h3 className="mb-2 text-lg font-bold text-ink">Сопряжение кассового терминала</h3>

        {/* Инструкция */}
        <div className="mb-4 rounded-card border border-amber-200 bg-amber-50 px-3 py-2.5 text-left">
          <p className="m-0 text-tiny leading-snug text-warnFg">
            Отсканируйте QR-код камерой мобильного устройства для активации кассы
            {companyName ? ' в организации ' : ''}
            {companyName ? <strong>{companyName}</strong> : ''}. Срок действия: 5 минут.
          </p>
        </div>

        {error && (
          <p className="mb-3 rounded-card border border-red-200 bg-red-50 px-3 py-2 text-small text-danger">
            {error}
          </p>
        )}

        {/* Контейнер QR-кода */}
        {timeLeft > 0 ? (
          <div className="mb-4 flex justify-center rounded-panel border border-line bg-white p-3">
            {qrSvg ? (
              <div className="h-[220px] w-[220px]" dangerouslySetInnerHTML={{ __html: qrSvg }} />
            ) : (
              <div className="flex h-[220px] items-center">Генерация QR...</div>
            )}
          </div>
        ) : (
          <div className="mb-4 flex h-[220px] flex-col items-center justify-center text-danger">
            <Clock size={24} className="mb-2 text-danger" />
            <strong>Срок действия истёк</strong>
            <span className="text-small text-muted">Закройте окно и откройте заново</span>
          </div>
        )}

        <div className="mb-5 text-small text-muted">
          Действителен ещё:{' '}
          <strong className={timeLeft < 60 ? 'text-danger' : 'text-ink'}>{timeFormatted}</strong>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="h-[46px] w-full rounded-panel border-0 bg-soft text-base2 font-semibold text-inkSoft"
        >
          Закрыть
        </button>
      </div>
    </div>
  );
};
